import { useTranslation } from 'react-i18next';
import { FileX } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ProjectFileItem } from './ProjectFileItem';
import { ProjectFileCard } from './ProjectFileCard';
import { ProjectFolderItem } from './ProjectFolderItem';
import type { ProjectFileDto, ProjectFolderNodeDto, ProjectFilesViewMode } from '../types';

interface ProjectFilesListProps {
  files: ProjectFileDto[];
  folders: ProjectFolderNodeDto[];
  directFolders: ProjectFolderNodeDto[];
  isLoading: boolean;
  viewMode: ProjectFilesViewMode;
  activeFolderId: string | null;
  selectedIds: Set<string>;
  onDelete: (fileId: string) => void;
  onMove: (fileId: string) => void;
  onRename: (fileId: string, currentName: string) => void;
  onToggleFavorite: (fileId: string, isFavorite: boolean) => void;
  onToggleVisibility: (fileId: string, isPrivate: boolean) => void;
  onSelect: (id: string, e: React.MouseEvent) => void;
  onPreview: (file: ProjectFileDto) => void;
  onOpenFolder: (id: string) => void;
  onRenameFolder: (id: string, name: string) => void;
  onMoveFolder: (id: string) => void;
  onDeleteFolder: (id: string) => void;
}

function ListSkeleton() {
  return (
    <div className="flex flex-col">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 border-b px-4 py-3">
          <Skeleton className="size-4 rounded" />
          <Skeleton className="size-5 rounded" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="hidden h-4 w-20 lg:block" />
          <Skeleton className="hidden h-4 w-28 lg:block" />
          <Skeleton className="hidden h-4 w-24 md:block" />
        </div>
      ))}
    </div>
  );
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-3 gap-3 p-4 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex flex-col overflow-hidden rounded-lg border">
          <Skeleton className="h-1 w-full rounded-none" />
          <div className="flex flex-col gap-2 p-3">
            <Skeleton className="h-5 w-20 rounded" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <div className="flex justify-between pt-1">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-3 w-12" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ProjectFilesList({
  files,
  folders,
  directFolders,
  isLoading,
  viewMode,
  activeFolderId,
  selectedIds,
  onDelete,
  onMove,
  onRename,
  onToggleFavorite,
  onToggleVisibility,
  onSelect,
  onPreview,
  onOpenFolder,
  onRenameFolder,
  onMoveFolder,
  onDeleteFolder,
}: ProjectFilesListProps) {
  const { t } = useTranslation('projectFiles');

  if (isLoading) {
    return viewMode === 'grid' ? <GridSkeleton /> : <ListSkeleton />;
  }

  const isEmpty = directFolders.length === 0 && files.length === 0;

  if (isEmpty) {
    const emptyKey = activeFolderId ? 'list.emptyFolder' : 'list.empty';
    return (
      <div
        className="flex flex-col items-center justify-center gap-3 py-16 text-center text-muted-foreground"
        data-testid="project-files-list-empty"
      >
        <FileX className="size-10 opacity-40" />
        <p className="text-sm">{t(emptyKey)}</p>
      </div>
    );
  }

  if (viewMode === 'grid') {
    return (
      <div
        className="grid grid-cols-3 gap-3 p-4 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
        data-testid="project-files-grid"
      >
        {directFolders.map((folder) => (
          <ProjectFolderItem
            key={folder.id}
            folder={folder}
            viewMode="grid"
            isSelected={!!folder.id && selectedIds.has(folder.id)}
            onOpen={onOpenFolder}
            onSelect={onSelect}
            onRename={onRenameFolder}
            onMove={onMoveFolder}
            onDelete={onDeleteFolder}
          />
        ))}
        {files.map((file) => (
          <ProjectFileCard
            key={file.id}
            file={file}
            folders={folders}
            isSelected={!!file.id && selectedIds.has(file.id)}
            onDelete={onDelete}
            onMove={onMove}
            onRename={onRename}
            onToggleFavorite={onToggleFavorite}
            onToggleVisibility={onToggleVisibility}
            onSelect={onSelect}
            onPreview={onPreview}
          />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="project-files-list">
      <div className="flex items-center gap-3 border-b px-4 py-2">
        <div className="size-4 shrink-0" />
        <span className="flex-1 text-xs font-medium text-muted-foreground">
          {t('list.columns.name')}
        </span>
        <span className="hidden text-xs font-medium text-muted-foreground lg:block w-20 text-right">
          {t('list.columns.size')}
        </span>
        <span className="hidden text-xs font-medium text-muted-foreground lg:block w-28">
          {t('list.columns.uploadedBy')}
        </span>
        <span className="hidden text-xs font-medium text-muted-foreground md:block w-24 text-right">
          {t('list.columns.date')}
        </span>
        <div className="w-[120px] shrink-0" />
      </div>

      {directFolders.map((folder) => (
        <ProjectFolderItem
          key={folder.id}
          folder={folder}
          viewMode="list"
          isSelected={!!folder.id && selectedIds.has(folder.id)}
          onOpen={onOpenFolder}
          onSelect={onSelect}
          onRename={onRenameFolder}
          onMove={onMoveFolder}
          onDelete={onDeleteFolder}
        />
      ))}

      {files.map((file) => (
        <ProjectFileItem
          key={file.id}
          file={file}
          folders={folders}
          isSelected={!!file.id && selectedIds.has(file.id)}
          onDelete={onDelete}
          onMove={onMove}
          onRename={onRename}
          onToggleFavorite={onToggleFavorite}
          onToggleVisibility={onToggleVisibility}
          onSelect={onSelect}
          onPreview={onPreview}
        />
      ))}
    </div>
  );
}
